import type { FilterData } from 'portlet/models';
import {
    areDatabricksQueryParameters,
    type DatabricksQueryTable,
} from 'portlet/models/query/systems/databricks/types';
import type { PortletQueryParams } from 'portlet/models/query/systems/types';

import type { QueryReplacer } from '.';
import { getColumnValue } from './utils';

// Searches for {filter(column1, column2...)} in query statement and applies proper
// WHERE clause condition into it based on dispatched filters (when column has no default value or assigned filter is not dispatched - skip)
// eg.
// For filters: COMPANY: Bayer, COUNTRY: DE
// filter(column1, column2, column3) => column1 = 'Bayer' AND column2 = 'DE'
export const FilterQueryReplacer: QueryReplacer = {
    regex: /\{filter\(\[([\w.,\s]+)\](,\s?([\w\s=!<>]+))?\)\}/,
    replace: (statement, tables, filterValues, regexMatches) => {
        if (
            !tables.length ||
            !regexMatches.length ||
            !regexMatches.every(item => item.group.length === 3)
        ) {
            return statement;
        }

        return regexMatches.reduce((pre, cur) => {
            const columns = cur.group[0].replace(/\s/g, '').split(',');
            const replacements = prepareWhereClauses(
                columns,
                cur.group[2] ?? 'true',
                tables,
                filterValues,
            );

            return pre.replace(cur.exact, replacements.join(' AND '));
        }, statement);
    },
};

// Works same as FilterQueryReplacer but as a vega expression
export const ac_sqlDbxWhere = (
    query: PortletQueryParams,
    columns: string[],
    fallback?: string,
): string => {
    if (!areDatabricksQueryParameters(query)) {
        return '';
    }
    const replacements = prepareWhereClauses(
        columns,
        fallback ?? 'true',
        query.metadata.tables,
        query.filters,
    );

    return replacements.join(' AND ');
};

function prepareWhereClauses(
    columns: string[],
    fallback: string,
    tables: DatabricksQueryTable[],
    filterValues: FilterData[],
): string[] {
    return columns
        .map(columnName => {
            const tableColumn = tables
                .map(table =>
                    table.columns.map(col => ({
                        ...col,
                        tableName: `${table.name}.${col.name}`,
                    })),
                )
                .flat()
                .find(item => item.name === columnName || item.tableName === columnName);

            if (!tableColumn) {
                return null;
            }
            const columnValue = getColumnValue(tableColumn, filterValues);
            const operator = tableColumn.filter?.whereClause?.operator ?? '=';
            const valueFunction = tableColumn.filter?.whereClause?.valueFunction;
            const wrapValue = (value: string, wrapper?: string): string =>
                wrapper ? `${wrapper}(${value})` : value;
            const wrappedColumnName = wrapValue(
                columnName,
                tableColumn.filter?.whereClause?.columnFunction,
            );

            switch (columnValue.filterType) {
                case null: {
                    if (columnValue.value === null) {
                        return null;
                    }

                    return `${wrappedColumnName} ${operator} ${wrapValue(
                        columnValue.value,
                        valueFunction,
                    )}`;
                }
                case 'DATE_RANGE': {
                    const customOperator = tableColumn.filter?.whereClause?.operator ?? 'BETWEEN';

                    return `${wrappedColumnName} ${customOperator} (${columnValue.value})`;
                }
                case 'HIERARCHY':
                case 'DIMENSION': {
                    if (columnValue.isValueArray) {
                        const customOperator = tableColumn.filter?.whereClause?.operator ?? 'IN';

                        return `${wrappedColumnName} ${customOperator} (${columnValue.value})`;
                    }

                    return `${wrappedColumnName} ${operator} ${wrapValue(
                        columnValue.value,
                        valueFunction,
                    )}`;
                }
                default: {
                    return `${wrappedColumnName} ${operator} ${wrapValue(
                        columnValue.value,
                        valueFunction,
                    )}`;
                }
            }
        })
        .map(item => item ?? fallback);
}
